import jsPDF from 'jspdf'
import { formatTime12, formatDate, today, daysAgo } from './dateUtils'
import { typeLabels } from './constants'
import { ageInMonths, estimatePercentile, ordinal } from './whoGrowthStandards'
import { renderGrowthChartPng, canDrawGrowthChart } from './growthChart'

const COLORS = {
  primary: [232, 108, 80],
  text: [44, 36, 32],
  muted: [107, 94, 84],
  light: [168, 152, 136],
  border: [236, 232, 225],
  green: [52, 166, 108],
  red: [217, 68, 82],
  white: [255, 255, 255],
  bg: [250, 248, 245],
}

const CAT_COLORS = {
  heart: [217, 68, 82],
  diuretic: [74, 143, 212],
  stomach: [139, 108, 193],
  blood: [212, 147, 10],
  other: [168, 152, 136],
}

// ===== Shared helpers =====

function formatFeedType(type) {
  return typeLabels[type] || type || ''
}

function pausesForSession(allPauses, sessionId) {
  return (allPauses || []).filter(p => p.sessionId === sessionId)
}

// On-pump minutes for a completed session (excludes disconnect time).
function sessionOnPumpMin(session, allPauses) {
  if (!session.endedAt) return 0
  const start = new Date(session.startedAt).getTime()
  const end = new Date(session.endedAt).getTime()
  const offMs = pausesForSession(allPauses, session.id).reduce((sum, p) => {
    if (!p.reconnectedAt) return sum
    return sum + (new Date(p.reconnectedAt).getTime() - new Date(p.disconnectedAt).getTime())
  }, 0)
  return Math.max(0, Math.round((end - start - offMs) / 60000))
}

function sessionDisconnectMin(session, allPauses) {
  const offMs = pausesForSession(allPauses, session.id).reduce((sum, p) => {
    if (!p.reconnectedAt) return sum
    return sum + (new Date(p.reconnectedAt).getTime() - new Date(p.disconnectedAt).getTime())
  }, 0)
  return Math.round(offMs / 60000)
}

// mL from actual reading, or estimated from rate × on-pump time.
// Returns { ml, isEstimate } or { ml: null, isEstimate: false } if unknowable.
function sessionMl(session, allPauses) {
  if (session.totalMlActual != null) return { ml: Number(session.totalMlActual), isEstimate: false }
  if (session.rateMlHr != null) {
    const onPumpMin = sessionOnPumpMin(session, allPauses)
    return { ml: Math.round((session.rateMlHr * onPumpMin) / 60), isEstimate: true }
  }
  return { ml: null, isEstimate: false }
}

function formatHM(minutes) {
  if (!minutes) return '0m'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  return `${h}h ${m}m`
}

// Sessions whose endedAt falls on the given date (matches Today's Feeds behavior)
function continuousSessionsForDate(sessions, date) {
  return (sessions || []).filter(s => s.endedAt && s.endedAt.slice(0, 10) === date)
}

// Compute totals (on-pump min, mL, session count) for a single date
function dailyContinuousTotals(sessions, pauses, date) {
  const day = continuousSessionsForDate(sessions, date)
  let onPumpMin = 0, totalMl = 0, hasEstimate = false
  for (const s of day) {
    onPumpMin += sessionOnPumpMin(s, pauses)
    const { ml, isEstimate } = sessionMl(s, pauses)
    if (ml != null) totalMl += ml
    if (isEstimate) hasEstimate = true
  }
  return { onPumpMin, totalMl, sessionCount: day.length, hasEstimate }
}

// Combined daily intake: bolus mL + continuous mL for a given date
function dailyIntakeMl(data, date) {
  const bolus = (data.feeds || []).filter(f => f.date === date).reduce((s, f) => s + Number(f.amount || 0), 0)
  const cont = dailyContinuousTotals(data.continuousFeedSessions, data.continuousFeedPauses, date)
  return { bolus, continuous: cont.totalMl, total: bolus + cont.totalMl, hasEstimate: cont.hasEstimate }
}

// Format a percentile label given a child + metric + measurement, or '' if no context
function percentileLabel(child, metric, dateStr, value) {
  const sex = child?.sex
  const dob = child?.date_of_birth
  if (!sex || !dob || sex === 'other' || value == null) return ''
  const months = ageInMonths(dob, dateStr)
  const p = estimatePercentile(metric, sex, months, Number(value))
  return p != null ? `~${ordinal(p)}` : ''
}

function addHeader(doc, title, childName) {
  doc.setFillColor(...COLORS.primary)
  doc.rect(0, 0, 210, 32, 'F')
  doc.setTextColor(...COLORS.white)
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.text(title, 14, 16)
  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')
  doc.text(childName, 14, 26)
  const dateStr = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  doc.text(dateStr, 196, 26, { align: 'right' })
  doc.setTextColor(...COLORS.text)
}

function addFooter(doc, page) {
  doc.setFontSize(7)
  doc.setTextColor(...COLORS.light)
  doc.text('Generated by Little Legend Tracker', 14, 287)
  doc.text(`Page ${page}`, 196, 287, { align: 'right' })
}

export function generateMedSchedule(medications, childName) {
  const doc = new jsPDF()
  addHeader(doc, 'Medication Schedule', childName)

  let y = 42
  function ensureSpace(needed) {
    if (y + needed > 280) {
      addFooter(doc, doc.internal.getNumberOfPages())
      doc.addPage()
      y = 20
    }
  }

  // Split into scheduled (have times) and PRN (no times — taken as-needed)
  const scheduled = (medications || []).filter(m => Array.isArray(m.times) && m.times.length > 0)
  const prn = (medications || []).filter(m => !Array.isArray(m.times) || m.times.length === 0)

  // ===== Scheduled medications =====
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Daily Schedule', 14, y)
  y += 10

  if (scheduled.length === 0) {
    doc.setFontSize(9)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(...COLORS.muted)
    doc.text('No scheduled medications.', 14, y)
    y += 10
  } else {
    const slots = {}
    scheduled.forEach(med => {
      med.times.forEach(t => {
        if (!slots[t]) slots[t] = []
        slots[t].push(med)
      })
    })
    const sortedTimes = Object.keys(slots).sort()

    // Table header
    doc.setFillColor(...COLORS.bg)
    doc.rect(14, y, 182, 8, 'F')
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.muted)
    doc.text('TIME', 16, y + 6)
    doc.text('MEDICATION', 48, y + 6)
    doc.text('DOSE', 110, y + 6)
    doc.text('CATEGORY', 142, y + 6)
    doc.text('INSTRUCTIONS', 170, y + 6)
    y += 12

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)

    sortedTimes.forEach(time => {
      slots[time].forEach(med => {
        ensureSpace(8)
        const catColor = CAT_COLORS[med.category] || CAT_COLORS.other
        doc.setFillColor(...catColor)
        doc.circle(18, y - 1, 2, 'F')

        doc.setTextColor(...COLORS.text)
        doc.setFont('helvetica', 'bold')
        doc.text(formatTime12(time) || '', 24, y)
        doc.setFont('helvetica', 'normal')
        doc.text(med.name || '', 48, y)
        doc.text(med.dose || '', 110, y)
        doc.setTextColor(...catColor)
        doc.text(med.category || '', 142, y)
        doc.setTextColor(...COLORS.muted)
        doc.text((med.instructions || '').substring(0, 20), 170, y)
        y += 8

        doc.setDrawColor(...COLORS.border)
        doc.line(14, y - 3, 196, y - 3)
      })
    })

    y += 4
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.muted)
    doc.text(`${scheduled.length} scheduled medication${scheduled.length !== 1 ? 's' : ''} across ${sortedTimes.length} time slot${sortedTimes.length !== 1 ? 's' : ''} per day`, 14, y)
    y += 10
  }

  // ===== PRN / As-needed medications =====
  if (prn.length > 0) {
    ensureSpace(20)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.text)
    doc.text('PRN / As-Needed', 14, y)
    y += 10

    // Table header — different columns from scheduled (no time column)
    doc.setFillColor(...COLORS.bg)
    doc.rect(14, y, 182, 8, 'F')
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.muted)
    doc.text('MEDICATION', 18, y + 6)
    doc.text('DOSE', 80, y + 6)
    doc.text('PURPOSE', 115, y + 6)
    doc.text('WHEN TO GIVE', 162, y + 6)
    y += 12

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    prn.forEach(med => {
      ensureSpace(10)
      const catColor = CAT_COLORS[med.category] || CAT_COLORS.other
      doc.setFillColor(...catColor)
      doc.circle(16, y - 1, 2, 'F')

      doc.setTextColor(...COLORS.text)
      doc.setFont('helvetica', 'bold')
      doc.text(med.name || '', 22, y)
      doc.setFont('helvetica', 'normal')
      doc.text(med.dose || '', 80, y)
      // Purpose may be longer — clip to fit column width
      doc.setTextColor(...COLORS.muted)
      doc.text((med.purpose || '').substring(0, 28), 115, y)
      doc.text((med.instructions || 'as needed').substring(0, 22), 162, y)
      y += 8
      doc.setDrawColor(...COLORS.border)
      doc.line(14, y - 3, 196, y - 3)
    })

    y += 4
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.muted)
    doc.text(`${prn.length} PRN medication${prn.length !== 1 ? 's' : ''}`, 14, y)
    y += 10
  }

  // Overall summary
  ensureSpace(8)
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text(`Total: ${medications.length} medication${medications.length !== 1 ? 's' : ''} (${scheduled.length} scheduled, ${prn.length} PRN)`, 14, y)

  addFooter(doc, doc.internal.getNumberOfPages())
  doc.save(`med-schedule-${childName.toLowerCase().replace(/\s+/g, '-')}.pdf`)
}

export function generateDailySummary(data, child, date) {
  const childName = typeof child === 'string' ? child : (child?.name || 'Child')
  const childObj = typeof child === 'string' ? null : child
  const d = date || today()
  const doc = new jsPDF()
  addHeader(doc, 'Daily Summary', `${childName} - ${formatDate(d)}`)

  let y = 42
  function ensureSpace(needed) {
    if (y + needed > 280) {
      addFooter(doc, doc.internal.getNumberOfPages())
      doc.addPage()
      y = 20
    }
  }

  // ===== Medications =====
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Medications', 14, y)
  y += 8

  const slots = {}
  data.medications.forEach(med => {
    med.times.forEach(t => {
      if (!slots[t]) slots[t] = []
      slots[t].push(med)
    })
  })

  let totalMeds = 0, givenMeds = 0
  Object.keys(slots).sort().forEach(time => {
    slots[time].forEach(med => {
      totalMeds++
      const key = med.id + '_' + time
      const given = data.medLog[d] && data.medLog[d][key]
      if (given) givenMeds++

      ensureSpace(7)
      doc.setFontSize(9)
      doc.setDrawColor(...COLORS.border)
      doc.rect(16, y - 4, 4, 4)
      if (given) {
        doc.setFillColor(...COLORS.green)
        doc.rect(16, y - 4, 4, 4, 'F')
        doc.setTextColor(...COLORS.white)
        doc.setFontSize(7)
        doc.text('\u2713', 17, y - 0.5)
      }

      doc.setTextColor(...COLORS.text)
      doc.setFontSize(9)
      doc.text(`${formatTime12(time) || ''} - ${med.name || ''} ${med.dose || ''}`, 24, y)
      if (given) {
        doc.setTextColor(...COLORS.green)
        doc.text(`Given at ${formatTime12(given.givenAt) || ''}${given.givenBy ? ' by ' + given.givenBy : ''}`, 120, y)
      } else {
        doc.setTextColor(...COLORS.red)
        doc.text('Not given', 120, y)
      }
      y += 7
    })
  })

  doc.setTextColor(...COLORS.text)
  doc.setFont('helvetica', 'bold')
  doc.text(`${givenMeds}/${totalMeds} medications given`, 14, y + 2)
  y += 14

  // ===== Feeds (bolus + continuous) =====
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Feeds', 14, y)
  y += 8

  const dayBolus = (data.feeds || []).filter(f => f.date === d).sort((a, b) => a.time.localeCompare(b.time))
  const dayContinuous = continuousSessionsForDate(data.continuousFeedSessions, d)
    .sort((a, b) => (a.endedAt || '').localeCompare(b.endedAt || ''))

  if (dayBolus.length === 0 && dayContinuous.length === 0) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(...COLORS.muted)
    doc.text('No feeds logged today', 14, y)
    y += 8
  } else {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    dayBolus.forEach(f => {
      ensureSpace(6)
      doc.setTextColor(...COLORS.text)
      doc.text(`${formatTime12(f.time)} - ${formatFeedType(f.type)} ${f.amount}mL`, 16, y)
      if (f.notes) {
        doc.setTextColor(...COLORS.muted)
        doc.text(String(f.notes).substring(0, 60), 90, y)
      }
      y += 6
    })

    dayContinuous.forEach(s => {
      ensureSpace(6)
      const onPump = sessionOnPumpMin(s, data.continuousFeedPauses)
      const off = sessionDisconnectMin(s, data.continuousFeedPauses)
      const { ml, isEstimate } = sessionMl(s, data.continuousFeedPauses)
      const startT = formatTime12(s.startedAt.slice(11, 16))
      const endT = formatTime12(s.endedAt.slice(11, 16))
      doc.setTextColor(...COLORS.text)
      doc.text(`${startT}\u2013${endT} - ${formatFeedType(s.feedType)} (continuous)`, 16, y)
      doc.setTextColor(...COLORS.muted)
      const detail = ml != null
        ? `${isEstimate ? '~' : ''}${ml}mL \u00b7 ${formatHM(onPump)} on pump${off ? ` \u00b7 ${formatHM(off)} off` : ''}`
        : `${formatHM(onPump)} on pump${off ? ` \u00b7 ${formatHM(off)} off` : ''}`
      doc.text(detail, 110, y)
      y += 6
    })

    const intake = dailyIntakeMl(data, d)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.text)
    const totalLabel = intake.continuous > 0
      ? `Total: ${intake.total}mL ${intake.hasEstimate ? '(includes estimates) ' : ''}\u2014 ${intake.bolus}mL bolus + ${intake.continuous}mL continuous`
      : `Total: ${intake.total}mL across ${dayBolus.length} feed${dayBolus.length !== 1 ? 's' : ''}`
    doc.text(totalLabel, 14, y + 2)
    y += 12
  }

  // ===== Latest Weight =====
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Latest Weight', 14, y)
  y += 8
  const latestWeight = data.weights?.length ? data.weights[data.weights.length - 1] : null
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  if (latestWeight) {
    doc.setTextColor(...COLORS.text)
    doc.text(`${Number(latestWeight.value).toFixed(2)}kg on ${formatDate(latestWeight.date)}`, 14, y)
    const pl = percentileLabel(childObj, 'weight', latestWeight.date, latestWeight.value)
    if (pl) {
      doc.setTextColor(...COLORS.primary)
      doc.text(`(${pl} percentile)`, 90, y)
    }
  } else {
    doc.setTextColor(...COLORS.muted)
    doc.text('No weight recorded', 14, y)
  }
  y += 10

  // ===== Latest Height =====
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Latest Height', 14, y)
  y += 8
  const latestHeight = data.heights?.length ? data.heights[data.heights.length - 1] : null
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  if (latestHeight) {
    doc.setTextColor(...COLORS.text)
    doc.text(`${Number(latestHeight.value).toFixed(1)}cm on ${formatDate(latestHeight.date)}`, 14, y)
    const pl = percentileLabel(childObj, 'length', latestHeight.date, latestHeight.value)
    if (pl) {
      doc.setTextColor(...COLORS.primary)
      doc.text(`(${pl} percentile)`, 90, y)
    }
  } else {
    doc.setTextColor(...COLORS.muted)
    doc.text('No height recorded', 14, y)
  }
  y += 12

  // ===== Custom Tracker Entries (today) =====
  const todayTrackerLogs = (data.trackerLogs || []).filter(l => l.date === d)
  if (todayTrackerLogs.length > 0) {
    ensureSpace(20)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.text)
    doc.text('Custom Tracker Entries', 14, y)
    y += 8

    // Group by tracker
    const byTracker = {}
    for (const log of todayTrackerLogs) {
      if (!byTracker[log.trackerId]) byTracker[log.trackerId] = []
      byTracker[log.trackerId].push(log)
    }

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    for (const [trackerId, logs] of Object.entries(byTracker)) {
      const tracker = (data.trackers || []).find(t => t.id === trackerId)
      if (!tracker) continue
      ensureSpace(6 + logs.length * 5)
      doc.setTextColor(...COLORS.text)
      doc.setFont('helvetica', 'bold')
      doc.text(`${tracker.icon || ''} ${tracker.name}`, 16, y)
      if (tracker.type === 'counter') {
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...COLORS.muted)
        doc.text(`${logs.length} time${logs.length !== 1 ? 's' : ''}`, 110, y)
        y += 6
      } else {
        y += 6
        const sorted = [...logs].sort((a, b) => a.time.localeCompare(b.time))
        sorted.forEach(l => {
          ensureSpace(5)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(...COLORS.text)
          const valueStr = `${l.value || ''}${tracker.unit ? ' ' + tracker.unit : ''}`
          doc.text(`  ${formatTime12(l.time)} \u2014 ${valueStr}`, 18, y)
          if (l.notes) {
            doc.setTextColor(...COLORS.muted)
            doc.text(String(l.notes).substring(0, 50), 110, y)
          }
          y += 5
        })
      }
    }
    y += 6
  }

  // ===== Notes =====
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Notes', 14, y)
  y += 8

  const dayNotes = (data.notes || []).filter(n => n.date === d)
  if (dayNotes.length === 0) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(...COLORS.muted)
    doc.text('No notes for this day', 14, y)
  } else {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    dayNotes.forEach(n => {
      ensureSpace(12)
      doc.setTextColor(...COLORS.muted)
      doc.text(`${formatTime12(n.time)}${n.loggedBy ? ' - ' + n.loggedBy : ''}`, 14, y)
      y += 5
      doc.setTextColor(...COLORS.text)
      const lines = doc.splitTextToSize(n.text, 170)
      doc.text(lines, 14, y)
      y += lines.length * 5 + 4
    })
  }

  addFooter(doc, doc.internal.getNumberOfPages())
  doc.save(`daily-summary-${childName.toLowerCase().replace(/\s+/g, '-')}-${d}.pdf`)
}

// Order roles in clinical-relevance order; anything else falls into 'Other'
const CONTACT_ROLE_ORDER = [
  'Cardiologist', 'Paediatrician', 'GP', 'Surgeon', 'Therapist',
  'Dietitian', 'Nurse', 'Pharmacy', 'Hospital', 'Other',
]

export function generateContactsReport(data, child) {
  const childName = typeof child === 'string' ? child : (child?.name || 'Child')
  const contacts = data.contacts || []
  const doc = new jsPDF()
  addHeader(doc, 'Medical Contacts', childName)

  let y = 42
  function ensureSpace(needed) {
    if (y + needed > 280) {
      addFooter(doc, doc.internal.getNumberOfPages())
      doc.addPage()
      y = 20
    }
  }

  if (contacts.length === 0) {
    doc.setFontSize(11)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(...COLORS.muted)
    doc.text('No contacts have been added yet. Add them in the Contacts tab.', 14, y)
    addFooter(doc, 1)
    doc.save(`contacts-${childName.toLowerCase().replace(/\s+/g, '-')}-${today()}.pdf`)
    return
  }

  // Group by role, preserve clinical ordering
  const byRole = {}
  for (const c of contacts) {
    const role = c.role || 'Other'
    if (!byRole[role]) byRole[role] = []
    byRole[role].push(c)
  }
  const roles = Object.keys(byRole).sort((a, b) => {
    const ai = CONTACT_ROLE_ORDER.indexOf(a)
    const bi = CONTACT_ROLE_ORDER.indexOf(b)
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
  })

  for (const role of roles) {
    const list = byRole[role].slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    ensureSpace(16)

    // Role header strip
    doc.setFillColor(...COLORS.bg)
    doc.rect(14, y, 182, 8, 'F')
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.primary)
    doc.text(`${role} (${list.length})`, 18, y + 6)
    y += 14

    for (const c of list) {
      // Estimate space: name + 1-3 detail lines + notes lines
      const detailParts = []
      if (c.phone) detailParts.push(`📞 ${c.phone}`)
      if (c.email) detailParts.push(`✉ ${c.email}`)
      const detailLine = detailParts.join('   ')
      const notesLines = c.notes ? doc.splitTextToSize(c.notes, 178) : []
      const blockHeight = 8 + (detailLine ? 5 : 0) + (c.location ? 5 : 0) + (notesLines.length * 5) + 4
      ensureSpace(blockHeight)

      // Name
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...COLORS.text)
      doc.text(c.name || '', 18, y)
      y += 6

      // Phone + email line
      if (detailLine) {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...COLORS.text)
        doc.text(detailLine, 22, y)
        y += 5
      }

      // Location
      if (c.location) {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...COLORS.muted)
        doc.text(c.location, 22, y)
        y += 5
      }

      // Notes
      if (notesLines.length > 0) {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(...COLORS.muted)
        doc.text(notesLines, 22, y)
        y += notesLines.length * 5
      }

      // Separator
      doc.setDrawColor(...COLORS.border)
      doc.line(14, y + 1, 196, y + 1)
      y += 6
    }
  }

  // Footer summary
  ensureSpace(8)
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text(`Total: ${contacts.length} contact${contacts.length !== 1 ? 's' : ''} across ${roles.length} role${roles.length !== 1 ? 's' : ''}`, 14, y)

  addFooter(doc, doc.internal.getNumberOfPages())
  doc.save(`contacts-${childName.toLowerCase().replace(/\s+/g, '-')}-${today()}.pdf`)
}

export function generateGrowthReport(data, child) {
  const childName = child?.name || 'Child'
  const sex = child?.sex
  const dob = child?.date_of_birth
  const hasContext = sex && dob && sex !== 'other'
  const sexLabel = sex === 'female' ? 'girls' : 'boys'

  const doc = new jsPDF()
  addHeader(doc, 'Growth Report', childName)
  let y = 42

  // ===== Latest measurements =====
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Latest Measurements', 14, y)
  y += 8

  const latestWeight = data.weights?.length ? data.weights[data.weights.length - 1] : null
  const latestHeight = data.heights?.length ? data.heights[data.heights.length - 1] : null

  // Weight tile
  doc.setFillColor(...COLORS.bg)
  doc.rect(14, y, 88, 28, 'F')
  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.muted)
  doc.text('WEIGHT', 18, y + 6)
  doc.setFontSize(18)
  doc.setTextColor(...COLORS.text)
  if (latestWeight) {
    doc.text(`${Number(latestWeight.value).toFixed(2)} kg`, 18, y + 18)
    if (hasContext) {
      const pl = percentileLabel(child, 'weight', latestWeight.date, latestWeight.value)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(...COLORS.primary)
      doc.text(pl ? `${pl} percentile` : '', 18, y + 24)
    }
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.muted)
    doc.text(formatDate(latestWeight.date), 96, y + 24, { align: 'right' })
  } else {
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.muted)
    doc.text('No data', 18, y + 18)
  }

  // Height tile
  doc.setFillColor(...COLORS.bg)
  doc.rect(108, y, 88, 28, 'F')
  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.muted)
  doc.text('HEIGHT', 112, y + 6)
  doc.setFontSize(18)
  doc.setTextColor(...COLORS.text)
  if (latestHeight) {
    doc.text(`${Number(latestHeight.value).toFixed(1)} cm`, 112, y + 18)
    if (hasContext) {
      const pl = percentileLabel(child, 'length', latestHeight.date, latestHeight.value)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(...COLORS.primary)
      doc.text(pl ? `${pl} percentile` : '', 112, y + 24)
    }
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.muted)
    doc.text(formatDate(latestHeight.date), 190, y + 24, { align: 'right' })
  } else {
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.muted)
    doc.text('No data', 112, y + 18)
  }

  y += 36

  // ===== 2-week trend =====
  function trendFor(metric, entries) {
    if (!hasContext || entries.length < 2) return null
    const latest = entries[entries.length - 1]
    const latestPct = estimatePercentile(metric, sex, ageInMonths(dob, latest.date), Number(latest.value))
    if (latestPct == null) return null
    const latestTime = new Date(latest.date + 'T00:00:00').getTime()
    const twoWeeks = latestTime - 14 * 24 * 60 * 60 * 1000
    let prev = null
    for (let i = entries.length - 2; i >= 0; i--) {
      const t = new Date(entries[i].date + 'T00:00:00').getTime()
      if (t <= twoWeeks) { prev = entries[i]; break }
      prev = entries[i]
    }
    const prevPct = prev ? estimatePercentile(metric, sex, ageInMonths(dob, prev.date), Number(prev.value)) : null
    return { latestPct, prevPct, prevDate: prev?.date }
  }

  const wTrend = trendFor('weight', data.weights || [])
  const hTrend = trendFor('length', data.heights || [])

  if (wTrend || hTrend) {
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.text)
    doc.text('Percentile Trend (last 2 weeks)', 14, y)
    y += 7
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')

    function drawTrendRow(label, t) {
      if (!t) return
      doc.setTextColor(...COLORS.text)
      doc.setFont('helvetica', 'bold')
      doc.text(`${label}:`, 16, y)
      doc.setFont('helvetica', 'normal')
      if (t.prevPct != null && t.prevDate !== data[label === 'Weight' ? 'weights' : 'heights'].slice(-1)[0]?.date) {
        const arrow = t.prevPct === t.latestPct ? '→' : t.latestPct > t.prevPct ? '↑' : '↓'
        doc.text(`${arrow} ${ordinal(t.prevPct)} → ${ordinal(t.latestPct)}`, 50, y)
        doc.setTextColor(...COLORS.muted)
        doc.text(`(since ${formatDate(t.prevDate)})`, 100, y)
      } else {
        doc.text(`Currently ${ordinal(t.latestPct)} percentile`, 50, y)
      }
      y += 6
    }
    drawTrendRow('Weight', wTrend)
    drawTrendRow('Height', hTrend)
    y += 4
  }

  // Reference standard note
  if (hasContext) {
    doc.setFontSize(8)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(...COLORS.muted)
    doc.text(`Percentiles based on WHO Child Growth Standards (${sexLabel}, 0-24 months).`, 14, y)
    y += 8
  } else {
    doc.setFontSize(8)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(...COLORS.muted)
    const reason = !dob ? "child's date of birth" : 'sex'
    doc.text(`Set ${reason} in Settings to see WHO percentile context.`, 14, y)
    y += 8
  }

  function ensureSpace(needed) {
    if (y + needed > 280) {
      addFooter(doc, doc.internal.getNumberOfPages())
      doc.addPage()
      y = 20
    }
  }

  // ===== Embedded charts =====
  // Render the on-screen growth chart to an off-screen canvas, then embed
  // the resulting PNG. Crispness via 2x dpr inside renderGrowthChartPng.
  function embedChart(title, entries, metric) {
    if (!canDrawGrowthChart(entries)) return
    ensureSpace(80)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.text)
    doc.text(title, 14, y)
    y += 4
    try {
      const dataUrl = renderGrowthChartPng({
        entries,
        metric,
        childSex: sex,
        childDob: dob,
        width: 1100,
        height: 420,
      })
      // Place at full content width (14..196 = 182mm), 70mm tall — same aspect as 1100×420
      doc.addImage(dataUrl, 'PNG', 14, y, 182, 70)
      y += 72
      if (hasContext) {
        doc.setFontSize(7)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(...COLORS.muted)
        doc.text('Bands: 3rd · 15th · 50th · 85th · 97th percentile (WHO)', 14, y)
        y += 6
      }
    } catch (err) {
      doc.setFontSize(8)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(...COLORS.muted)
      doc.text('Chart could not be rendered.', 14, y + 4)
      y += 10
    }
  }

  embedChart('Weight Chart', data.weights || [], 'weight')
  embedChart('Height Chart', data.heights || [], 'length')

  // ===== History tables =====
  function drawHistoryTable(title, entries, unit, metric, decimals) {
    if (!entries || entries.length === 0) return
    ensureSpace(20)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.text)
    doc.text(title, 14, y)
    y += 7
    // Table header
    doc.setFillColor(...COLORS.bg)
    doc.rect(14, y, 182, 7, 'F')
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.muted)
    doc.text('DATE', 16, y + 5)
    doc.text('VALUE', 70, y + 5)
    if (hasContext) doc.text('PERCENTILE', 110, y + 5)
    doc.text('AGE', 160, y + 5)
    y += 10

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    // Most recent first
    const reversed = [...entries].reverse()
    for (const e of reversed) {
      ensureSpace(7)
      doc.setTextColor(...COLORS.text)
      doc.text(formatDate(e.date), 16, y)
      doc.text(`${Number(e.value).toFixed(decimals)} ${unit}`, 70, y)
      if (hasContext) {
        const pct = estimatePercentile(metric, sex, ageInMonths(dob, e.date), Number(e.value))
        if (pct != null) {
          doc.setTextColor(...COLORS.primary)
          doc.text(`${ordinal(pct)}`, 110, y)
        }
      }
      doc.setTextColor(...COLORS.muted)
      const months = dob ? ageInMonths(dob, e.date) : null
      if (months != null) {
        const m = Math.floor(months)
        const w = Math.round((months - m) * 4.345)
        doc.text(`${m}m ${w}w`, 160, y)
      }
      y += 6
      doc.setDrawColor(...COLORS.border)
      doc.line(14, y - 2, 196, y - 2)
    }
    y += 6
  }

  drawHistoryTable('Weight History', data.weights || [], 'kg', 'weight', 2)
  drawHistoryTable('Height History', data.heights || [], 'cm', 'length', 1)

  addFooter(doc, doc.internal.getNumberOfPages())
  doc.save(`growth-report-${childName.toLowerCase().replace(/\s+/g, '-')}-${today()}.pdf`)
}

// Inclusive date iteration helper. Yields each YYYY-MM-DD between from and to.
function datesBetween(from, to) {
  const out = []
  if (!from || !to || from > to) return out
  let cur = new Date(from + 'T00:00:00')
  const end = new Date(to + 'T00:00:00')
  while (cur <= end) {
    out.push(cur.toISOString().slice(0, 10))
    cur.setDate(cur.getDate() + 1)
  }
  return out
}

export function generateWeeklyReport(data, child, range) {
  const childName = typeof child === 'string' ? child : (child?.name || 'Child')
  const childObj = typeof child === 'string' ? null : child
  const doc = new jsPDF()
  // Default range: last 7 days. Custom range honored when both endpoints valid.
  const endDate = range?.to || today()
  const startDate = range?.from || daysAgo(6)
  const allDates = datesBetween(startDate, endDate)
  // Iterate from oldest to newest for tables, and reverse where we want newest-first
  const dayCount = allDates.length
  const rangeLabel = dayCount === 7
    ? `${childName} - ${formatDate(startDate)} to ${formatDate(endDate)}`
    : `${childName} - ${formatDate(startDate)} to ${formatDate(endDate)} (${dayCount} day${dayCount !== 1 ? 's' : ''})`
  addHeader(doc, dayCount === 7 ? 'Weekly Report' : 'Period Report', rangeLabel)

  let y = 42
  function ensureSpace(needed) {
    if (y + needed > 280) {
      addFooter(doc, doc.internal.getNumberOfPages())
      doc.addPage()
      y = 20
    }
  }

  // ===== Overall medication compliance + per-med breakdown =====
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Medication Compliance', 14, y)
  y += 10

  let totalDoses = 0, givenDoses = 0
  // Per-med tally: { medId: { name, times: [], expected, given } }
  const perMed = {}
  for (const d of allDates) {
    data.medications.forEach(med => {
      if (!perMed[med.id]) perMed[med.id] = { name: med.name, expected: 0, given: 0 }
      med.times.forEach(t => {
        totalDoses++
        perMed[med.id].expected++
        const key = med.id + '_' + t
        if (data.medLog[d] && data.medLog[d][key]) {
          givenDoses++
          perMed[med.id].given++
        }
      })
    })
  }

  const compliance = totalDoses > 0 ? Math.round((givenDoses / totalDoses) * 100) : 0
  doc.setFontSize(24)
  doc.setTextColor(...(compliance >= 90 ? COLORS.green : compliance >= 70 ? COLORS.primary : COLORS.red))
  doc.text(`${compliance}%`, 14, y + 2)
  doc.setFontSize(9)
  doc.setTextColor(...COLORS.muted)
  doc.text(`${givenDoses} of ${totalDoses} doses given`, 42, y)
  y += 12

  // Per-medication breakdown
  const medBreakdown = Object.values(perMed).filter(m => m.expected > 0)
  if (medBreakdown.length > 0) {
    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.muted)
    ensureSpace(10)
    doc.setFillColor(...COLORS.bg)
    doc.rect(14, y, 182, 7, 'F')
    doc.text('MEDICATION', 16, y + 5)
    doc.text('GIVEN', 130, y + 5)
    doc.text('%', 175, y + 5, { align: 'right' })
    y += 10
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    medBreakdown.sort((a, b) => a.given / a.expected - b.given / b.expected).forEach(m => {
      ensureSpace(7)
      const pct = Math.round((m.given / m.expected) * 100)
      doc.setTextColor(...COLORS.text)
      doc.text(m.name, 16, y)
      doc.text(`${m.given}/${m.expected}`, 130, y)
      doc.setTextColor(...(pct >= 90 ? COLORS.green : pct >= 70 ? COLORS.primary : COLORS.red))
      doc.text(`${pct}%`, 175, y, { align: 'right' })
      y += 6
      doc.setDrawColor(...COLORS.border)
      doc.line(14, y - 2, 196, y - 2)
    })
    y += 6
  }

  // ===== Daily intake (bolus + continuous) =====
  ensureSpace(60)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Daily Intake', 14, y)
  y += 8

  doc.setFillColor(...COLORS.bg)
  doc.rect(14, y, 182, 7, 'F')
  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.muted)
  doc.text('DATE', 16, y + 5)
  doc.text('BOLUS', 60, y + 5)
  doc.text('CONTINUOUS', 100, y + 5)
  doc.text('TOTAL', 150, y + 5)
  doc.text('ON PUMP', 175, y + 5)
  y += 10

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  let weekHasContinuous = false
  let weekHasEstimate = false
  for (const d of allDates) {
    const intake = dailyIntakeMl(data, d)
    const cont = dailyContinuousTotals(data.continuousFeedSessions, data.continuousFeedPauses, d)
    if (intake.continuous > 0) weekHasContinuous = true
    if (cont.hasEstimate) weekHasEstimate = true
    ensureSpace(6)
    doc.setTextColor(...COLORS.text)
    doc.text(formatDate(d), 16, y)
    doc.text(`${intake.bolus}mL`, 60, y)
    doc.text(intake.continuous > 0 ? `${cont.hasEstimate ? '~' : ''}${intake.continuous}mL` : '—', 100, y)
    doc.setFont('helvetica', 'bold')
    doc.text(`${intake.total}mL`, 150, y)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.muted)
    doc.text(cont.onPumpMin > 0 ? formatHM(cont.onPumpMin) : '—', 175, y)
    y += 6
  }
  if (weekHasEstimate) {
    doc.setFontSize(7)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(...COLORS.muted)
    doc.text('~ continuous-feed mL estimated from rate × on-pump time when no actual reading entered', 14, y)
    y += 6
  }
  if (!weekHasContinuous) {
    // Drop the "continuous" + "on pump" columns mentally — but the table is rendered;
    // a small note keeps the report self-explanatory for clinicians.
  }
  y += 6

  // ===== Weight trend =====
  ensureSpace(40)
  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.text)
  doc.text('Weight Trend', 14, y)
  y += 8

  const weekWeights = (data.weights || []).filter(w => w.date >= startDate && w.date <= endDate)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  if (weekWeights.length === 0) {
    doc.setTextColor(...COLORS.muted)
    doc.text('No weights recorded this week', 14, y)
    y += 8
  } else {
    weekWeights.forEach(w => {
      ensureSpace(6)
      doc.setTextColor(...COLORS.text)
      doc.text(`${formatDate(w.date)}: ${Number(w.value).toFixed(2)}kg`, 16, y)
      const pl = percentileLabel(childObj, 'weight', w.date, w.value)
      if (pl) {
        doc.setTextColor(...COLORS.primary)
        doc.text(`(${pl} percentile)`, 90, y)
      }
      y += 6
    })
    if (weekWeights.length >= 2) {
      const first = Number(weekWeights[0].value)
      const last = Number(weekWeights[weekWeights.length - 1].value)
      const change = last - first
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...(change >= 0 ? COLORS.green : COLORS.red))
      doc.text(`Change: ${change >= 0 ? '+' : ''}${change.toFixed(2)}kg`, 14, y + 2)
      y += 8
    }
  }
  y += 4

  // ===== Height trend =====
  const weekHeights = (data.heights || []).filter(h => h.date >= startDate && h.date <= endDate)
  if (weekHeights.length > 0) {
    ensureSpace(20)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.text)
    doc.text('Height Trend', 14, y)
    y += 8
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    weekHeights.forEach(h => {
      ensureSpace(6)
      doc.setTextColor(...COLORS.text)
      doc.text(`${formatDate(h.date)}: ${Number(h.value).toFixed(1)}cm`, 16, y)
      const pl = percentileLabel(childObj, 'length', h.date, h.value)
      if (pl) {
        doc.setTextColor(...COLORS.primary)
        doc.text(`(${pl} percentile)`, 90, y)
      }
      y += 6
    })
    if (weekHeights.length >= 2) {
      const change = Number(weekHeights[weekHeights.length - 1].value) - Number(weekHeights[0].value)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...(change >= 0 ? COLORS.green : COLORS.red))
      doc.text(`Change: ${change >= 0 ? '+' : ''}${change.toFixed(1)}cm`, 14, y + 2)
      y += 10
    }
  }

  // ===== Notes from the period =====
  // Show any notes the parent wrote during the range — these are the
  // most signal-dense entries for a clinician (tagged moments, concerns,
  // questions, observations).
  const periodNotes = (data.notes || [])
    .filter(n => n.date >= startDate && n.date <= endDate)
    .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time))
  if (periodNotes.length > 0) {
    ensureSpace(20)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.text)
    doc.text(`Notes (${periodNotes.length})`, 14, y)
    y += 8
    for (const n of periodNotes) {
      const lines = doc.splitTextToSize(n.text || '', 178)
      ensureSpace(6 + lines.length * 5 + 3)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(...COLORS.muted)
      doc.text(`${formatDate(n.date)} · ${formatTime12(n.time)}${n.loggedBy ? ' · ' + n.loggedBy : ''}`, 14, y)
      y += 5
      doc.setFontSize(9)
      doc.setTextColor(...COLORS.text)
      doc.text(lines, 16, y)
      y += lines.length * 5 + 4
    }
    y += 2
  }

  // ===== Activity log (filtered to non-routine events) =====
  // Excludes individual 'med given' entries — those are already reflected in
  // the per-medication compliance table above. Keeps weight, height, feed,
  // tracker, and other notable entries. Capped at the most recent 40.
  const periodActivity = (data.activityLog || [])
    .filter(a => {
      if (!a.timestamp) return false
      const dateStr = a.timestamp.slice(0, 10)
      if (dateStr < startDate || dateStr > endDate) return false
      // Drop noisy "Gave X" / "Unmarked X" routine med events
      if (a.type === 'med' && /^(Gave|Unmarked) /.test(a.message || '')) return false
      return true
    })
    .slice(0, 40)

  if (periodActivity.length > 0) {
    ensureSpace(20)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.text)
    doc.text(`Activity Timeline (${periodActivity.length})`, 14, y)
    y += 4
    doc.setFontSize(7)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(...COLORS.muted)
    doc.text('Routine medication doses excluded (see Medication Compliance above).', 14, y + 4)
    y += 8

    for (const a of periodActivity) {
      const ts = a.timestamp ? new Date(a.timestamp) : null
      const dateLabel = ts
        ? `${formatDate(ts.toISOString().slice(0, 10))} · ${ts.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}`
        : ''
      const lines = doc.splitTextToSize(a.message || '', 130)
      ensureSpace(5 + Math.max(0, lines.length - 1) * 4)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(...COLORS.muted)
      doc.text(dateLabel, 14, y)
      doc.setTextColor(...COLORS.text)
      doc.text(lines, 64, y)
      y += Math.max(5, lines.length * 4 + 1)
    }
  }

  addFooter(doc, doc.internal.getNumberOfPages())
  doc.save(`weekly-report-${childName.toLowerCase().replace(/\s+/g, '-')}-${endDate}.pdf`)
}
